import React from "react";

// Square mascot/logo tile for a coach. Renders the uploaded image when one
// exists, otherwise a colored square with the coach's first initial.
//
// Props:
//   imageDataUrl  string|null  Data URL of the mascot image (optional)
//   coachName     string       The coach's display name (used for fallback +
//                              alt text)
//   size          number       Pixel size (square edge). Defaults to 96.
//   borderRadius  string       CSS border-radius. Defaults to "12px" — small
//                              softening, still very much a square.
//   className     string       Optional extra className for layout overrides
//
// The palette is deterministic so a coach always gets the same color.

const FALLBACK_PALETTE = [
  "#2563eb", "#16a34a", "#dc2626", "#9333ea", "#ea580c",
  "#0891b2", "#ca8a04", "#db2777", "#0d9488", "#7c3aed",
  "#65a30d", "#e11d48", "#0284c7", "#a16207", "#4f46e5",
];

const colorForName = (name) => {
  const text = (name || "?").toLowerCase();
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }
  return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];
};

const MascotLogo = ({
  imageDataUrl,
  coachName,
  size = 96,
  borderRadius = "12px",
  className,
  style: extraStyle,
}) => {
  const trimmed = (coachName || "").trim();
  const initial = trimmed ? trimmed[0].toUpperCase() : "?";
  const baseStyle = {
    // Shrink gracefully on narrow containers (mobile) while never exceeding
    // the intended max size. aspect-ratio keeps it a perfect square.
    width: `min(${size}px, 100%)`,
    aspectRatio: "1 / 1",
    maxWidth: `${size}px`,
    borderRadius,
    flexShrink: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    border: "2px solid #2e4a78",
    background: "#0c1a36",
    fontWeight: 800,
    color: "#ffffff",
    letterSpacing: "0.02em",
    ...(extraStyle || {}),
  };

  if (imageDataUrl) {
    return (
      <div className={className} style={baseStyle} aria-label={`${trimmed} mascot`}>
        <img
          src={imageDataUrl}
          alt={`${trimmed || "Coach"} mascot`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
    );
  }

  const initialStyle = {
    ...baseStyle,
    background: colorForName(trimmed),
    fontSize: `${Math.max(12, Math.round(size * 0.45))}px`,
  };
  return (
    <div className={className} style={initialStyle} aria-label={`${trimmed} mascot placeholder`}>
      {initial}
    </div>
  );
};

export default MascotLogo;
